import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Spin } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';

import { Routes } from '@share/constants';
import { getLandingPageByTemplateIdAsync } from '@share/api';
import { BlueButton } from '@share/components';
import type { ILandingPageRenderData } from '@share/common-types';
import { FunctionalRootState } from '@share/utils';
import { fetchCertificateTemplateById } from '@share/store/slices';
import { useCertificateClubSiteId } from '@share/components';

import { CertificateTemplateDetailView } from '../certificate-template-detail-view';
import { CertificateLandingPage } from '../landing-page';

import styles from './styles.module.scss';

export function CertificateTemplateDetailWrapper(): React.ReactElement {
  const intl = useIntl();
  const history = useHistory();
  const dispatch = useDispatch();
  const { id, accountUsername } = useParams<{ id: string; accountUsername?: string }>();
  const { certificateTemplate, loading, error } = useSelector(
    (state: FunctionalRootState) => state.CertificateTemplateByIdStore,
  );
  const organizationId = useCertificateClubSiteId();
  const [landingPage, setLandingPage] = useState<ILandingPageRenderData | null>(null);
  const [landingPageLoading, setLandingPageLoading] = useState(false);

  const certificatesPath = accountUsername
    ? `/${accountUsername}${Routes.Certificates}`
    : Routes.Certificates;
  const paymentPathBase = accountUsername
    ? `/${accountUsername}${Routes.Certificates}`
    : Routes.Certificates;

  const fetchCertificateTemplate = useCallback(() => {
    if (id) {
      dispatch(fetchCertificateTemplateById({ id, clubSiteId: organizationId }));
    }
  }, [dispatch, id, organizationId]);

  useEffect(() => {
    fetchCertificateTemplate();
  }, [fetchCertificateTemplate]);

  useEffect(() => {
    let cancelled = false;

    if (!id) {
      setLandingPage(null);
      setLandingPageLoading(false);
      return undefined;
    }

    setLandingPage(null);
    setLandingPageLoading(true);
    getLandingPageByTemplateIdAsync(id, organizationId)
      .then(({ data }) => {
        if (!cancelled) {
          setLandingPage(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLandingPage(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLandingPageLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [id, organizationId]);

  const handleBackClick = useCallback(() => {
    history.push(certificatesPath);
  }, [history, certificatesPath]);

  const handleReloadClick = useCallback(() => {
    fetchCertificateTemplate();
  }, [fetchCertificateTemplate]);

  const handleBuyClick = useCallback(() => {
    if (id) {
      history.push(`${paymentPathBase}/${id}/payment`);
    }
  }, [history, id, paymentPathBase]);

  const isNotAvailable = error?.code === 'certificate_unavailable';
  const backLabel = intl.formatMessage({ id: 'certificates.backTo' });

  if (loading || landingPageLoading) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.stateBlock} role="status" aria-busy="true">
          <Spin size="large" />
          <span className={styles.loadingText}>
            <FormattedMessage id="certificates.loading" />
          </span>
        </div>
      </div>
    );
  }
  if (error || !certificateTemplate) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.content}>
          <a
            href={certificatesPath}
            className={styles.backLink}
            onClick={(e) => {
              e.preventDefault();
              handleBackClick();
            }}
            aria-label={backLabel}
          >
            {backLabel}
          </a>
          <div className={styles.stateBlock}>
            <p className={styles.errorText}>
              {isNotAvailable
                ? intl.formatMessage({ id: 'certificates.noLongerAvailable' })
                : error?.title ?? intl.formatMessage({ id: 'certificates.notFound' })}
            </p>
            {!isNotAvailable && (
              <BlueButton onClick={handleReloadClick}>
                <FormattedMessage id="certificates.reloadCertificate" />
              </BlueButton>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (landingPage?.id && landingPage.isActive) {
    const paymentPath = `${paymentPathBase}/${id}/payment`;
    return (
      <CertificateLandingPage
        data={landingPage}
        paymentPath={paymentPath}
        certificatesPath={certificatesPath}
        backLabel={backLabel}
        onBackClick={handleBackClick}
        onBuyClick={handleBuyClick}
      />
    );
  }

  return (
    <CertificateTemplateDetailView
      name={certificateTemplate.name}
      validityMonths={certificateTemplate.validityMonths}
      totalPrice={certificateTemplate.totalPrice}
      savings={certificateTemplate.savings}
      itemTemplates={certificateTemplate.itemTemplates}
      eligibleCruiseLines={certificateTemplate.eligibleCruiseLines}
      imageUrl={certificateTemplate.imageUrl}
      url={certificateTemplate.url}
      descriptionContent={certificateTemplate.descriptionContent}
      averageRetailPrice={certificateTemplate.averageRetailPrice}
      onBackClick={handleBackClick}
      onBuyClick={handleBuyClick}
    />
  );
}
